import React, { useEffect, useState } from "react";
import { Landmark, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Panel, Skeleton } from "./ui";
import TaxSettings from "./TaxSettings";
import Hint from "./Hint";
import { api } from "../lib/api";
import { useMoney } from "../context/MoneyContext";

function Row({ label, value, strong, tone }) {
  const toneCls = tone === "rose" ? "text-rose-600" : tone === "green" ? "text-emerald-600" : "text-slate-700";
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-slate-500">{label}</span>
      <span className={`tabular ${strong ? "font-semibold" : ""} ${toneCls}`}>{value}</span>
    </div>
  );
}

// Optional, collapsed-by-default estimate of the German tax due on unrealized gains
// across current holdings, split by asset category since stocks/ETFs, crypto and
// fund-linked insurance policies are each taxed under different rules.
export default function TaxImpactPanel() {
  const { t } = useTranslation();
  const { marginalTaxRate, churchTaxPct, married, display } = useMoney();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.taxEstimate({
      marginal_rate_pct: marginalTaxRate, church_tax_pct: churchTaxPct,
      sparerpauschbetrag: married ? 2000 : 1000,
    }).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [open, marginalTaxRate, churchTaxPct, married]);

  const hasAnything = data && (data.stocks_and_funds.gross_gain > 0 || data.crypto.short_term_gain > 0 || data.crypto.long_term_gain_tax_free > 0);

  return (
    <div>
      <button data-testid="toggle-tax-impact" onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700 transition-colors">
        <Landmark size={15} /> {t("tax.impactToggle")}
        <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <Panel className="card p-4 sm:p-6 mt-3" testid="tax-impact-panel">
          <div className="flex items-center gap-2 mb-4">
            <Landmark size={18} className="text-brand-500" />
            <h3 className="font-heading font-semibold tracking-tight text-lg">{t("tax.impactTitle")}</h3>
          </div>

          <TaxSettings testid="holdings-tax-settings" />

          {loading ? (
            <Skeleton className="h-40 mt-4" />
          ) : !hasAnything ? (
            <div className="text-sm text-slate-400 text-center py-8 mt-2">{t("tax.noHoldings")}</div>
          ) : (
            <div className="mt-4 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-100 p-4">
                  <Hint term="Abgeltungssteuer"><div className="text-sm font-semibold mb-3">{t("tax.stocksAndFunds")}</div></Hint>
                  <div className="space-y-1.5 text-sm">
                    <Row label={t("tax.grossGain")} value={display(data.stocks_and_funds.gross_gain).primary} />
                    <Row label={t("tax.allowanceUsed")} value={display(data.stocks_and_funds.allowance_used).primary} />
                    <Row label={t("tax.effectiveRate")} value={`${data.stocks_and_funds.effective_rate_pct}%`} />
                    <Row label={t("tax.estimatedTax")} value={display(data.stocks_and_funds.tax_estimate).primary} strong tone="rose" />
                  </div>
                </div>
                <div className="rounded-xl border border-slate-100 p-4">
                  <Hint term="Freigrenze"><div className="text-sm font-semibold mb-3">{t("tax.crypto")}</div></Hint>
                  <div className="space-y-1.5 text-sm">
                    <Row label={t("tax.shortTermGain")} value={display(data.crypto.short_term_gain).primary} />
                    <Row label={t("tax.longTermTaxFree")} value={display(data.crypto.long_term_gain_tax_free).primary} tone="green" />
                    <div className="text-xs text-slate-400">{data.crypto.below_freigrenze ? t("tax.belowFreigrenze") : t("tax.aboveFreigrenze")}</div>
                    <Row label={t("tax.estimatedTax")} value={display(data.crypto.tax_estimate).primary} strong tone="rose" />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl bg-rose-50">
                <span className="font-medium text-rose-700">{t("tax.totalEstimatedTax")}</span>
                <span className="text-xl font-heading font-semibold tabular text-rose-700">{display(data.total_tax_estimate).primary}</span>
              </div>

              <div className="text-[11px] text-slate-400">{t("tax.disclaimer")}</div>
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}
